/**
 * shell/ResearchVariablePage.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * One addressable page per tracked variable — /research/variable/:variableId.
 * Replaces the fullscreen-modal idea from the first pass at the squares grid:
 * a real route gives a bookmarkable/shareable URL, a working browser back
 * button, and room for a short methodology note next to the chart, the way
 * a study would present one finding per page rather than popping a dialog
 * over the dashboard.
 *
 * One component reads off constants/researchVariables.js instead of 17
 * hand-built pages — adding variable #18 later is one new registry entry.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { DOMAIN_IDS, INDICATORS_BY_DOMAIN, PANCHAKOSHA } from "@/constants/ncfFramework";
import { TRACKED_VARIABLES, VARIABLE_BY_ID, FAMILIES, QS_CLUSTERS } from "@/constants/researchVariables";
import { API, MultiLineChart, CATEGORY_COLORS, selectStyle, groupSeriesFrom } from "./ResearchShared";

export default function ResearchVariablePage() {
  const { variableId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const varDef = VARIABLE_BY_ID[variableId];

  const [kosha,       setKosha]       = useState(PANCHAKOSHA[0].id);
  const [domainId,    setDomainId]    = useState(DOMAIN_IDS[0]);
  const [indicatorId, setIndicatorId] = useState(INDICATORS_BY_DOMAIN[DOMAIN_IDS[0]][0].id);
  const [cluster,     setCluster]     = useState(QS_CLUSTERS[0]?.id || '');
  const [school,      setSchool]      = useState('');
  const [schools,     setSchools]     = useState([]);
  const [ageBand,     setAgeBand]     = useState('');
  const [isActive,    setIsActive]    = useState('');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    fetch(`${API}/schools`, { headers: getAuthHeader() }).then(r => r.json())
      .then(d => setSchools(d.schools || d.data || [])).catch(() => {});
  }, []);

  // Reset the kosha/indicator selector back to a sane default whenever the
  // variable itself changes (e.g. jumping here from the quick-switcher) —
  // otherwise a leftover indicatorId from a different domain could silently
  // 400 against the new variable's expectations.
  useEffect(() => {
    setKosha(PANCHAKOSHA[0].id);
    setDomainId(DOMAIN_IDS[0]);
    setIndicatorId(INDICATORS_BY_DOMAIN[DOMAIN_IDS[0]][0].id);
    setCluster(QS_CLUSTERS[0]?.id || '');
  }, [variableId]);

  useEffect(() => {
    if (!varDef) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (school)                params.set('school', school);
    if (isActive !== '')       params.set('isActive', isActive);
    if (ageBand)                params.set('ageBand', ageBand);
    if (varDef.needsKosha)      params.set('kosha', kosha);
    if (varDef.needsIndicator)  params.set('indicatorId', indicatorId);
    if (varDef.needsCluster)    params.set('cluster', cluster);

    fetch(`${API}/research/group/${variableId}?${params.toString()}`, { headers: getAuthHeader() })
      .then(r => r.json()).then(d => { if (d.success) setData(d.series); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [variableId, school, isActive, ageBand, kosha, indicatorId, cluster]);

  if (!varDef) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF' }}>
        Unknown variable "{variableId}".{' '}
        <Link to="/research" style={{ color: 'var(--navy)' }}>Back to the dashboard.</Link>
      </div>
    );
  }

  const { lines, byCategory } = data ? groupSeriesFrom(data) : { lines: [], byCategory: {} };
  const family = FAMILIES[varDef.family];

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: 1100, margin: '0 auto', fontFamily: "'Inter',system-ui,sans-serif" }}>
      <div style={{ marginBottom: 18 }}>
        <Link to="/research" style={{ fontSize: 12, color: 'var(--mist)', textDecoration: 'none' }}>← Across-Group Dashboard</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, color: family?.color || '#9CA3AF',
              background: `${family?.color || '#9CA3AF'}18`, padding: '3px 9px', borderRadius: 20 }}>
              {family?.label || varDef.family}
            </span>
          </div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 900, color: 'var(--navy)', lineHeight: 1.1 }}>
            {varDef.label}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{varDef.unit}</div>
        </div>

        <select value={variableId} onChange={e => navigate(`/research/variable/${e.target.value}`)} style={{ ...selectStyle, fontWeight: 700 }}>
          {Object.entries(FAMILIES).map(([famKey, fam]) => (
            <optgroup key={famKey} label={fam.label}>
              {TRACKED_VARIABLES.filter(v => v.family === famKey).map(v => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {(varDef.description || varDef.methodology) && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px', marginBottom: 18, fontSize: 12.5, color: '#374151', lineHeight: 1.6 }}>
          {varDef.description && <div>{varDef.description}</div>}
          {varDef.methodology && <div style={{ marginTop: 6, color: '#6B7280' }}><strong style={{ color: '#374151' }}>How it's computed: </strong>{varDef.methodology}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {varDef.needsKosha && (
          <select value={kosha} onChange={e => setKosha(e.target.value)} style={selectStyle}>
            {PANCHAKOSHA.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        )}
        {varDef.needsIndicator && (
          <>
            <select value={domainId} onChange={e => { setDomainId(e.target.value); setIndicatorId(INDICATORS_BY_DOMAIN[e.target.value][0].id); }} style={selectStyle}>
              {DOMAIN_IDS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={indicatorId} onChange={e => setIndicatorId(e.target.value)} style={selectStyle}>
              {INDICATORS_BY_DOMAIN[domainId].map(i => <option key={i.id} value={i.id}>{i.id} — {i.label}</option>)}
            </select>
          </>
        )}
        {varDef.needsCluster && (
          <select value={cluster} onChange={e => setCluster(e.target.value)} style={selectStyle}>
            {QS_CLUSTERS.map(cl => <option key={cl.id} value={cl.id}>{cl.icon} {cl.label}</option>)}
          </select>
        )}
        <select value={school} onChange={e => setSchool(e.target.value)} style={selectStyle}>
          <option value="">All schools</option>
          {schools.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <select value={ageBand} onChange={e => setAgeBand(e.target.value)} style={selectStyle}>
          <option value="">All ages</option>
          {['<4', '4-5', '5-6', '6-7', '7+'].map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={isActive} onChange={e => setIsActive(e.target.value)} style={selectStyle}>
          <option value="">Active + inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', padding: '18px 20px' }}>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
          every line is one child, colored by disability category · never averaged
        </div>
        {loading ? (
          <div style={{ padding: 80, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
        ) : (
          <MultiLineChart series={lines} width={1000} height={420} thin yDomain={varDef.yDomain} />
        )}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
          {Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: CATEGORY_COLORS[cat] || '#9CA3AF' }} />
              <span style={{ fontSize: 11, color: '#374151' }}>{cat} ({items.length})</span>
            </div>
          ))}
          {!loading && Object.keys(byCategory).length === 0 && (
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>No children match the current filters.</span>
          )}
        </div>
      </div>
    </div>
  );
}